import { Component, signal } from '@angular/core';

import { RouterLink } from '@angular/router';
import { AuiIconComponent } from '@ral/ui:aui/icons';
import { StoryCardComponent } from '@storyteller/card';
import { StoryContainerComponent } from '@storyteller/container';
import { StoryDescriptionComponent } from '@storyteller/description';
import { StoryFeatureComponent } from '@storyteller/feature';
import { StoryImageComponent, StoryImageSourceComponent } from '@storyteller/image';
import { StoryTitleComponent } from '@storyteller/title';
import { MillBarFeederComponent } from '../components';
import { AcpSystemComponent } from '../components/acp-system.component';
import { AntBotCncComponent } from '../components/antbot-pro-cnc.component';
import { InquiryComponent, InquiryItemComponent } from '../components/inquiry.component';
import { AppSimplyCarouselComponent } from '../components/simply-carousel.component';
import { AuiItemComponent } from '@ral/ui:aui/item';

@Component({
    selector: 'app-easy-lean-kit-page',
    imports: [
        StoryContainerComponent,
        StoryTitleComponent,
        StoryDescriptionComponent,
        StoryImageComponent,
        AuiItemComponent,
        InquiryComponent,
        MillBarFeederComponent,
        AntBotCncComponent,
        AcpSystemComponent,
        AcpSystemComponent,
        StoryFeatureComponent,
        InquiryItemComponent,
        AuiIconComponent,
        RouterLink,
        AppSimplyCarouselComponent,
        StoryImageSourceComponent,
        StoryCardComponent,
    ],
    template: `
        <header>
            <story-container
                [space]="'py-[20px] w500:py-[40px] md:py-[70px] lg:py-[100px]'"
                [skipTopPadding]="true"
                [skipSmallScreenTopPadding]="true"
            >
                <div class="detail-cover gap-6 md:gap-10">
                    <div class="img-block">
                        <story-image
                            fetchPriority="high"
                            [loading]="'eager'"
                            class="block aspect-[345/435] max-w-[500px] overflow-hidden rounded sm:aspect-[500/600]"
                            aria-labelledby="easy-lean-kit-title"
                        >
                            <story-source
                                [src]="'assets/images/easy-lean-kit.jpg'"
                                [width]="500"
                                [height]="600"
                                media="(min-width: 640px)"
                            />
                            <story-source
                                [src]="'assets/images/easy-lean-kit.jpg'"
                                [width]="500"
                                [height]="600"
                                media="(min-width: 200px)"
                            />
                        </story-image>
                    </div>

                    <div class="title-block">
                        <div class="flex flex-col gap-2" id="easy-lean-kit-title">
                            <story-title [headingLevel]="1" className="story-font-headline1 pb-3 w500:pb-4"
                                >Easy lean kit
                            </story-title>
                            <story-description>
                                Sada pro vícenásobné upínání. Soubor mechanických prvků a&nbsp;praktických doporučení
                                pro levné a&nbsp;snadné&nbsp;vytížení CNC stroje.&nbsp;Vícenásobným upínáním se docílí
                                delšího běhu stroje bez obsluhy, efektivnější práci operátora, snížení výrobního času na
                                kus, odstranění nevýrobních časů stroje jako je manipulace, čištění a další.
                            </story-description>
                        </div>
                        <div class="flex flex-col gap-2 pb-[11px] pt-6 md:gap-2 md:pt-8">
                            <div class="flex flex-col gap-2 w460:flex-row">
                                <div>
                                    <button auiItem [size]="'40'" [color]="'primary'" (click)="openInquiryForm()">
                                        <span auiItemLabel>Zobrazit cenu</span>
                                    </button>
                                </div>
                                <a
                                    auiItem
                                    [size]="'40'"
                                    [color]="'gray'"
                                    auiStyle="decent"
                                    [routerLink]="['/', 'easy-lean-kit']"
                                    fragment="description"
                                >
                                    <span auiItemLabel>Chci více informací</span>
                                </a>
                            </div>
                            <div class="flex flex-row gap-1">
                                <aui-icon [icon]="'circle-info'" class="text-[12px]" />
                                <p class="aui-font-regular-12">
                                    Ke každé zaslané poptávce obdržíte kontrolní seznam pro správnou instalaci a
                                    používání výrobního přípravku včetně tipů z praxe.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="features-block self-end">
                        <div class="w500:grid-cols-2 grid grid-cols-1 gap-5 lg:gap-10">
                            <div>
                                <story-feature>
                                    <aui-icon icon="thumbs-up" class="text-[28px]" />
                                    <story-title [headingLevel]="2" className="story-font-headline3 pb-1"
                                        >Jednoduchý vstup do automatizace
                                    </story-title>
                                    <story-description>
                                        První krok k&nbsp;automatizaci vašeho pracoviště bez nákladných úprav
                                        a&nbsp;dalšího zabraného místa.
                                    </story-description>
                                </story-feature>
                            </div>
                            <div>
                                <story-feature>
                                    <aui-icon icon="gauge-max" class="text-[28px]" />
                                    <story-title [headingLevel]="2" className="story-font-headline3 pb-1"
                                        >Rychlé a&nbsp;cenově dostupné řešení
                                    </story-title>
                                    <story-description>
                                        Easy lean kit vám přinese výsledky rychle a&nbsp;za minimální investici.
                                    </story-description>
                                </story-feature>
                            </div>
                        </div>
                    </div>
                </div>
            </story-container>
        </header>
        <section>
            <div class="-top:5 w500:-top-12 relative">
                <div id="description" class="relative -top-12 lg:top-[-100px]"></div>
            </div>
            <story-container [skipTopPadding]="true" [skipSmallScreenTopPadding]="true">
                <div class="flex flex-col-reverse items-center gap-5 md:grid md:grid-cols-2 lg:gap-10">
                    <div>
                        <story-title [headingLevel]="2" className="story-font-headline2 pb-6"
                            >Produktový popis
                        </story-title>
                        <story-description class="block">
                            Easy lean kit je nejrychlejší a&nbsp;cenově výhodná cesta, jak zapojit stroj do noční práce.
                            Univerzální upínání kvádrových polotovarů umožní obrábět desítky dílů najednou, a&nbsp;to
                            bez další zástavby. Soubor mechanických prvků a&nbsp;praktických doporučení vás provede
                            přípravou, aby byla výroba stabilní a&nbsp;opakovatelná.
                            <br /><br />
                            Náš Easy lean kit se snadno skladuje, jednoduše se přenáší mezi pracovišti a&nbsp;dá se
                            upravit pro vaše rozměry. Po pevném upnutí na paletě spustíte cyklus, zavřete dílnu
                            a&nbsp;ráno odebíráte hotové díly.
                        </story-description>
                    </div>
                    <div>
                        <story-image class="aspect-[1.2662037037] overflow-hidden rounded">
                            <story-source [src]="'assets/images/easy-lean-kit.png'" [width]="547" [height]="432" />
                        </story-image>
                    </div>
                </div>
            </story-container>
        </section>

        <section class="overflow-hidden">
            <story-container [space]="'w500:py-10'" [skipTopPadding]="true" [skipSmallScreenTopPadding]="true">
                <app-carousel
                    [slides]="[
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9581.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 1',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9582.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 2',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9609.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 3',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9623.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 4',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9648.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 5',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9660.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 6',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9662.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 7',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9663.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 8',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9672.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 9',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9680.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 10',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/FM1A5570.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 11',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9575.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 12',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9726.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 13',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9735.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 14',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9764.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 15',
                        },
                        {
                            url: 'assets/images/easy-lean-kit/0K2A9769.jpg',
                            isPortrait: false,
                            alt: 'Easy lean kit | Fotka 16',
                        },
                    ]"
                ></app-carousel>
            </story-container>
        </section>
        <section>
            <story-container [space]="'pt-12 w500:py-10'" [skipTopPadding]="true" [skipSmallScreenTopPadding]="true">
                <story-title [headingLevel]="3" className="story-font-headline2 pb-6"
                    >Technická specifikace
                </story-title>

                <table class="w-full table-fixed text-left">
                    <thead>
                        <tr>
                            <th class="pb-2 pt-6 font-semibold">Parametr</th>
                            <th aria-label="Hodnota"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="tr">
                            <th>Materiál a povrch</th>
                            <td>ocel kalená</td>
                        </tr>
                        <tr class="tr">
                            <th>Dosah</th>
                            <td>2000&nbsp;mm</td>
                        </tr>
                        <tr class="tr">
                            <th>Doporučený utahovací moment</th>
                            <td>18&nbsp;Nm</td>
                        </tr>
                        <tr class="tr">
                            <th>Upínací síla</th>
                            <td>15,5&nbsp;kN</td>
                        </tr>
                        <tr class="tr">
                            <th>Čas přípravy segmentu</th>
                            <td>30&nbsp;s</td>
                        </tr>
                    </tbody>
                </table>

                <h4 class="pb-2 pt-6 font-semibold">Technický popis</h4>
                <story-description class="block max-w-[780px]"> 1 segment obsahuje:</story-description>
                <ul class="block list-inside list-disc p-2">
                    <li>6x šroub M6,</li>
                    <li>6x v-coil M6</li>
                    <li>4x upínací kámen</li>
                    <li>1x dorazový kámen</li>
                </ul>
                <story-description class="block max-w-[780px]">
                    Upínací kámen má ostře definovanou hranu, která se při upnutí lehce zakousne do povrchu obrobku
                    a&nbsp;tím zajišťuje jeho stabilní fixaci pro vysokovýkonné obrábění. Dorazový kámen je navržen
                    s&nbsp;pozitivním dosedacím úhlem, který poskytuje spolehlivé a&nbsp;opakovatelné upnutí obrobku
                    (viz detail).
                </story-description>
            </story-container>
        </section>
        <section>
            <story-container [space]="'pt-12 w500:py-10'" [skipTopPadding]="true" [skipSmallScreenTopPadding]="true">
                <story-title [headingLevel]="3" className="story-font-headline2 pb-6">Ke stažení</story-title>
                <div class="flex flex-wrap gap-6 lg:gap-8">
                    <div class="min-w-[245px] max-w-[320px] flex-grow">
                        <story-card>
                            <aui-icon icon="file-pdf" class="text-[32px]" />
                            <story-title [headingLevel]="4" className="@lg:story-font-headline4 story-font-headline4"
                                >Technická specifikace
                            </story-title>
                            <story-description
                                [className]="'@lg:leading-[28px] @lg:max-w-[460px] aui-font-regular-14 leading-[1.5]'"
                            >
                                Tento soubor obsahuje rozměry a technické specifikace pro Easy lean kit.
                            </story-description>
                            <a
                                auiItem
                                [size]="'40'"
                                [color]="'primary'"
                                [auiStyle]="'decent'"
                                [fullWidth]="true"
                                [href]="'/assets/files/easy-lean-kit_tech-spec.pdf'"
                                download
                                aria-label="Stáhnout specifikaci (PDF, méně než 1 MB)"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span auiItemLabel>Stáhnout specifikaci</span>
                            </a>
                        </story-card>
                    </div>
                    <div class="min-w-[245px] max-w-[320px] flex-grow">
                        <story-card>
                            <aui-icon icon="file-pdf" class="text-[32px]" />
                            <story-title [headingLevel]="4" className="@lg:story-font-headline4 story-font-headline4"
                                >Technický výkres
                            </story-title>
                            <story-description
                                [className]="'@lg:leading-[28px] @lg:max-w-[460px] aui-font-regular-14 leading-[1.5]'"
                            >
                                Tento soubor obsahuje technický výkres pro Easy lean kit.
                            </story-description>
                            <a
                                [href]="'/assets/files/easy-lean-kit_tech-vykres.pdf'"
                                auiItem
                                [size]="'40'"
                                [color]="'primary'"
                                [fullWidth]="true"
                                [auiStyle]="'decent'"
                                download
                                aria-label="Stáhnout výkres (PDF, méně než 1 MB)"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span auiItemLabel>Stáhnout výkres</span>
                            </a>
                        </story-card>
                    </div>
                </div>
            </story-container>
        </section>
        <section>
            <story-container>
                <story-title [headingLevel]="2" className="story-font-headline1 pb-12 md:pb-16 lg:pb-20"
                    >Prohlédněte si ostatní produkty
                </story-title>
                <div class="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <app-mill-bar-feeder></app-mill-bar-feeder>
                    </div>

                    <div>
                        <app-antbot-pro-cnc></app-antbot-pro-cnc>
                    </div>

                    <div>
                        <app-acp-system></app-acp-system>
                    </div>
                </div>
            </story-container>
        </section>
        @if (isInquiryFormOpened()) {
            <app-inquiry-form (closeInquiry)="closeInquiryForm()" [productName]="'Easy lean kit'">
                <app-inquiry-item productName="Easy lean kit (8 segmentů)" [priceWithoutVat]="21350"></app-inquiry-item>
                <p class="story-gray-text pb-2 pr-16">
                    Součástí kitu jsou dva extra upínací kameny, sada v-coil pro zvýšení životnosti závitů, 3D model
                    vzorové desky a technická podpora.
                </p>
                <app-inquiry-item
                    productName="Dodatečný segment"
                    [priceWithoutVat]="2998"
                    [defaultValue]="0"
                    [allowZeroItems]="true"
                ></app-inquiry-item>
            </app-inquiry-form>
        }
    `,
})
export class EasyLeanKitPageComponent {
    isInquiryFormOpened = signal<boolean>(false);

    openInquiryForm() {
        this.isInquiryFormOpened.set(true);
    }

    closeInquiryForm() {
        this.isInquiryFormOpened.set(false);
    }
}
